import type { Navigation, NavigationLink, NavigationSubMenu } from './types';
import { RoleHierarchy } from './types';
import type { UserPermissionType } from '../services/AuthService/types';
import type { SecurityOptIn } from '@shipium-corp/shipium-ts-types';
import { getShipiumDomain } from '~/utils/shipiumDomain';

const V1_MENU = 'V1_Menu';
const V2_MENU = 'V2_Menu';
const DEV_MENU_PATH = 'shipium-dev';

export class NavigationBuilder {
  private navigation: Navigation = [];
  private v1v2Menu = {
    name: 'default',
    path: 'vmenu',
    group: 'VMenu',
    links: [] as NavigationSubMenu[],
  };

  private isEu: boolean;

  constructor(
    private partnerUrlSlug: string,
    private userRole: UserPermissionType,
    private isShipiumUser?: boolean,
    private partnerFeatureToggles: Record<string, string> = {},
    private partnerOptins: Record<string, SecurityOptIn> = {},
    isEu?: boolean,
  ) {
    this.isEu = isEu ?? getShipiumDomain(window.location.hostname).isEu;
  }

  public addLink(link: NavigationLink) {
    if (link.external) {
      this.navigation.push({ ...link, path: link.path });
      return this;
    }
    this.navigation.push({
      ...link,
      path: `/${this.partnerUrlSlug}/${link.path}`,
    });
    return this;
  }

  public addSubMenu(subMenu: NavigationLink, subMenuLinks: NavigationSubMenu[]) {
    const processLinksRecursive = (
      links: NavigationSubMenu[],
      parentPath = `/${this.partnerUrlSlug}/${subMenu.path}`,
    ): NavigationSubMenu[] => {
      return links
        .filter((link) => (link.shipiumAdmin && this.isShipiumUser) || !link.shipiumAdmin)
        .map((link) => {
          const { links: nestedLinks, ...rest } = link;

          let fullPath = `${parentPath}/${link.path}`;
          if (link.external) {
            fullPath = link.path;
          } else if (subMenu.path === DEV_MENU_PATH) {
            fullPath = `/${this.partnerUrlSlug}/${link.path}`;
          }

          if (nestedLinks) {
            return {
              ...rest,
              path: fullPath,
              links: processLinksRecursive(nestedLinks, fullPath),
            };
          } else if (link.ignoreSubMenuPath) {
            return { ...rest, path: `/${this.partnerUrlSlug}/${link.path}` };
          } else {
            return { ...rest, path: fullPath };
          }
        });
    };

    const processedLinks = processLinksRecursive(subMenuLinks);

    this.navigation.push({
      ...subMenu,
      path: `/${this.partnerUrlSlug}/${subMenu.path}`,
      links: processedLinks,
    });
    return this;
  }

  public addAdminMenu(subMenu: NavigationLink, subMenuLinks: NavigationSubMenu[], includeV1V2Menu?: boolean) {
    if (this.isShipiumUser) {
      if (includeV1V2Menu) {
        this.v1v2Menu = {
          ...this.v1v2Menu,
          name:
            !this.partnerFeatureToggles.UI_ORTHO_V2 || this.partnerFeatureToggles.UI_ORTHO_V2 === 'false'
              ? V2_MENU
              : V1_MENU,
        };
        subMenuLinks.push(this.v1v2Menu);
      }
      return this.addSubMenu(subMenu, subMenuLinks);
    }
    return this;
  }

  private checkMissingFeature(
    partnerFeatureMap: Record<string, boolean>,
    requiredFeatureMap: Record<string, boolean>,
  ): boolean {
    return !!Object.keys(requiredFeatureMap || {}).find((val: any) => {
      return (
        requiredFeatureMap &&
        ((partnerFeatureMap.hasOwnProperty(val) && partnerFeatureMap[val] !== requiredFeatureMap[val]) ||
          (!partnerFeatureMap.hasOwnProperty(val) && !!requiredFeatureMap[val]))
      );
    });
  }

  // remove menu or sub menu based on required features + user permissions
  private filterAllowedItem(
    link: NavigationLink,
    featureMap: Record<string, boolean>,
    optinMap: Record<string, SecurityOptIn>,
  ) {
    if (this.isShipiumUser === false && link.shipiumAdmin === true) {
      return null;
    }
    if (link.hideInEu && this.isEu) {
      return null;
    }
    const userHasRequiredRole =
      !link.requiredPermission || RoleHierarchy[link.requiredPermission] <= RoleHierarchy[this.userRole];
    const partnerHasRequiredFeature =
      !link.requiredFeatureMap || !this.checkMissingFeature(featureMap, link.requiredFeatureMap);
    const partnerHasRequiredOptins =
      !link.requiredOptinMap ||
      !this.checkMissingFeature(
        Object.fromEntries(Object.entries(optinMap).map(([key, value]) => [key, value.active])),
        link.requiredOptinMap,
      );
    if (userHasRequiredRole && partnerHasRequiredFeature && partnerHasRequiredOptins) {
      return link;
    }

    // always push to shipium user menu
    // this will add them to the menu as we add links to the builder
    this.v1v2Menu.links.push(link);

    return null;
  }

  public filterAllowed() {
    const featureMap = Object.entries(this.partnerFeatureToggles || {})?.reduce((prev: Record<string, boolean>, e) => {
      const [key, val] = e;
      prev[key] = val === 'true' ? true : false;
      prev[key] = val === 'false' ? false : true;
      return prev;
    }, {});

    const filterRecursive = (menu: NavigationSubMenu) => {
      if (menu.links) {
        menu.links = menu.links
          .filter((l) => this.filterAllowedItem(l, featureMap, this.partnerOptins))
          .map((subMenu) => filterRecursive(subMenu));
      }
      return menu;
    };

    this.navigation = this.navigation
      .filter((n) => this.filterAllowedItem(n, featureMap, this.partnerOptins))
      .map((menu) => filterRecursive(menu))
      // this is to avoid adding any top level menus if the user
      // does not have required permissions for any of the submenu links.
      // if there were no links in the first place we want to keep the menu
      // this allows menu items created with addLink, like the print label tool,
      // to remain in place but removes tracking if the user does not have any of the tracking optins
      .filter((menu) => !menu.links || menu?.links?.length);

    return this;
  }

  public build() {
    // find v1/v2 menu and replace with the correct one
    const findV1V2Menu = (navigation: Navigation): Navigation => {
      const existingPaths: Record<string, boolean> = {};
      return navigation
        .filter((link) => {
          // this is specifically to support rendering simulation in the main nav
          // for admins but not show it twice once it is released
          const include = !link.path || !existingPaths[link.path];
          existingPaths[link.path] = true;
          return include;
        })
        .map((nav) => {
          if (nav.name === V1_MENU || nav.name === V2_MENU) {
            return this.v1v2Menu;
          } else if (nav.links) {
            return { ...nav, links: findV1V2Menu(nav.links) };
          } else {
            return nav;
          }
        });
    };

    this.navigation = findV1V2Menu(this.navigation);
    return this.navigation;
  }

  public static getPageHeaderPropsForPath(navigation: Navigation, path: string) {
    for (const link of navigation) {
      if (link.external) {
        return {
          parent: null,
          link,
        };
      }
      const children = (link as NavigationSubMenu).links;

      if (children) {
        const match = children.find((child) => child.path === path);
        if (match) {
          return { parent: link, link: match };
        }
      } else if (link.path === path) {
        return { parent: null, link };
      } else {
        console.log(link.path, path);
      }
    }
    return { parent: null, link: null };
  }
}
